import React, { FunctionComponent, useCallback, useLayoutEffect } from 'react'
import { useMeasure } from 'react-use'
import classNames from 'classnames/bind'
import { NavBarProps } from './NavBar.model'
import styles from './NavBar.module.scss'
import { IconButton, MaintenanceBanner } from '../../../shared'
import { atom, useSetAtom } from 'jotai'
import { useGoBack } from '../../../../hooks/utils/useGoBack.hook'

export const mobileMenuOpenAtom = atom(false)

const cx = classNames.bind(styles)

export const BackButton: FunctionComponent<{
  path: string | null
  keepSearchParams?: boolean
  onClick?: () => void
  title?: string
}> = ({ path, onClick, keepSearchParams, title = 'Retour' }) => {
  // `/` lands on the home page of the current role: it is the only sensible destination left when
  // there is no in-app history to go back to (direct access or page reload)
  const goBack = useGoBack('/')

  return (
    <div className={path === null ? styles.backButtonContainer : styles.backLinkContainer}>
      <IconButton
        onClick={() => {
          if (path === null) {
            if (onClick) {
              onClick()
              return
            } else {
              goBack()
            }
          }
        }}
        link={keepSearchParams ? `${path}${document.location.search}` : path!}
        icon="back"
        title={title}
        theme="dark"
        size="normal"
        testId="button-navigation-go_back"
        borderSize={path === null ? undefined : 2}
        noBorder={false}
      />
    </div>
  )
}

export const NavBar: FunctionComponent<NavBarProps> = ({
  backButtonPath,
  onBackButtonClick,
  backButtonTitle,
  keepSearchParams,
  children,
  stickyHeaderContent,
  noDivider = false,
}) => {
  const [ref, measure] = useMeasure()
  const setMobileMenuOpen = useSetAtom(mobileMenuOpenAtom)

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--navbar-height', `${measure.height}px`)
    return () => {
      document.documentElement.style.removeProperty('--navbar-height')
    }
  }, [measure.height])

  const handleOpenMobileMenu = useCallback(() => {
    setMobileMenuOpen(true)
  }, [setMobileMenuOpen])

  return (
    <div className={styles.container} ref={ref}>
      <span className={styles.backButtonWithPageTitle}>
        {(backButtonPath !== undefined || onBackButtonClick) && (
          <BackButton
            path={backButtonPath && !onBackButtonClick ? backButtonPath : null}
            title={backButtonTitle}
            onClick={onBackButtonClick || undefined}
            keepSearchParams={keepSearchParams}
          />
        )}
        <div className={styles.title}>{children}</div>
        <div className={styles.menuMobileContainer}>
          <IconButton
            icon="menu"
            theme="transparent"
            size="micro"
            onClick={handleOpenMobileMenu}
            testId="button-navigation-mobile_menu-open"
          />
        </div>
      </span>
      <div className={cx(styles.divider, { noDivider })} />
      <div>
        <MaintenanceBanner />
        {stickyHeaderContent}
      </div>
    </div>
  )
}
